
import { FloatingFeature, StringFeature } from "@models/FloatingFeature";

import { 
  UpgradableNumber,
  StartEquipmentChoice,
  RollPlusUpgradable,
  Modifier,
  WeaponLimitation,
  Potence,
  AbilityLimitation,
  SpellLimitation,
  AbilityEffect,
  // LimitationConstructor,
  Limitations,
  Ability,
  SpellModifier,
  SpellsFromList,
  SpellDamageOptions,
  CharacterLimitation,
  CreatureLimitation
} from "@dndModels";
import { AbilityScoreExpandedType, AbilityScoreType, AttackType, CastingTime, CreatureType, DamageType, DataType, FeatTier, HappensWhen, isDamageType, isFeatTier, isStatType, PotenceType, SelectableType, SizeType, SpellLevel, TimeRestriction, WeaponType } from "@dndModels/Types";
import { TIME_RESTRICTIONS } from "@dndModels/Constants";
import { Feature } from "@models";

export class UpgradableNumberFeature extends FloatingFeature {
  num: UpgradableNumber = new UpgradableNumber();

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.num = obj.num ? new UpgradableNumber(obj.num) : new UpgradableNumber();
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.num = obj.num ? new UpgradableNumber(obj.num) : new UpgradableNumber();
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      num: this.num.toDBObj()
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof UpgradableNumberFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: UpgradableNumberFeature) {
    this.num = copyMe.num.clone();
  }

  clone(drop_ids = false): UpgradableNumberFeature {
    const new_obj = new UpgradableNumberFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class FeatFeature extends FloatingFeature {
  standard = true;
  feat_ids: string[] = [];
  suggested = "";
  feat_tiers: FeatTier[] = []; // Ignored if standard = true
  feat_tags: string[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.feat_ids = obj.feat_ids ? [...obj.feat_ids] : [];
      this.standard = obj.standard !== undefined ? obj.standard : true;
      if (!this.standard) {
        this.suggested = obj.suggested ? obj.suggested : "";
        this.feat_tiers = [];
        let feat_tiers: string[] = obj.feat_tiers ? obj.feat_tiers : [];
        if (feat_tiers.includes("Standard")) {
          feat_tiers = feat_tiers.filter(o => o !== "Standard");
          feat_tiers.push("General");
        }
        this.feat_tags = obj.feat_tags ? obj.feat_tags : [];
        for (const feat_tier of feat_tiers) {
          if (isFeatTier(feat_tier)) {
            if (!this.feat_tiers.includes(feat_tier)) {
              this.feat_tiers.push(feat_tier);
            }
          } else {
            if (!this.feat_tags.includes(feat_tier)) {
              this.feat_tags.push(feat_tier);
            }
          }
        }
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.feat_ids = obj.feat_ids ? [...obj.feat_ids] : [];
  //     this.standard = obj.standard !== undefined ? obj.standard : true;
  //     if (!this.standard) {
  //       this.suggested = obj.suggested ? obj.suggested : "";
  //       this.feat_tiers = obj.feat_tiers ? obj.feat_tiers : [];
  //       if ((this.feat_tiers as string[]).includes("Standard")) {
  //         this.feat_tiers = (this.feat_tiers as string[]).filter(o => o !== "Standard") as FeatTier[];
  //         this.feat_tiers.push("General");
  //       }
  //     }
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      standard: this.standard,
      feat_ids: this.feat_ids,
      suggested: this.suggested,
      feat_tiers: this.feat_tiers,
      feat_tags: this.feat_tags
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof FeatFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: FeatFeature) {
    this.standard = copyMe.standard;
    this.feat_ids = [...copyMe.feat_ids];
    this.suggested = copyMe.suggested;
    this.feat_tiers = [...copyMe.feat_tiers];
    this.feat_tags = [...copyMe.feat_tags];
  }

  clone(drop_ids = false): FeatFeature {
    const new_obj = new FeatFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class ConditionFeature extends FloatingFeature {
  condition_id = "";
  level: UpgradableNumber = new UpgradableNumber(1);
  immunity = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.condition_id = obj.condition_id ? obj.condition_id : "";
      this.level = obj.level ? new UpgradableNumber(obj.level) : new UpgradableNumber(1);
      this.immunity = obj.immunity ? obj.immunity : false;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.condition_id = obj.condition_id ? obj.condition_id : "";
  //     this.level = obj.level ? new UpgradableNumber(obj.level) : new UpgradableNumber(1);
  //     this.immunity = obj.immunity ? obj.immunity : false;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      condition_id: this.condition_id,
      level: this.level.toDBObj(),
      immunity: this.immunity
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof ConditionFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: ConditionFeature) {
    this.condition_id = copyMe.condition_id;
    this.level = copyMe.level.clone();
    this.immunity = copyMe.immunity;
  }

  clone(drop_ids = false): ConditionFeature {
    const new_obj = new ConditionFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * This Feature makes it so a character will use a different Condition from a standard one.
 * It was made for the Malfeasant Wizard's Increased Threshold feature, 
 * which makes it so they can have twice as many levels of Contamination, 
 * and have symptoms at half the normal rate.
 * 
 * If a character has this feature, then the original condition is removed from the condition options on bar 3
 * and is replaced with the alternate.
 * Also any feature bases that look for the original condition will accept the alternate,
 * and the char_util.value method will accept the alternate if a ugn is looking for the original as a source.
 */
export class AlternateConditionFeature extends FloatingFeature {
  original_condition_id = "";
  alternate_condition_id = "";
  // This number gets multiplied by the level of the new condition to determine 
  // the equivalent level of the old condition.
  // So if the multiplier is 0.5 and something happens at level 4 of the old condition, 
  // then it happens at level 8 of the new condition.
  equivalent_level_multiplier = 1; 
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.original_condition_id = obj.original_condition_id ? obj.original_condition_id : "";
      this.alternate_condition_id = obj.alternate_condition_id ? obj.alternate_condition_id : "";
      this.equivalent_level_multiplier = obj.equivalent_level_multiplier !== undefined ? obj.equivalent_level_multiplier : 1;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.original_condition_id = obj.original_condition_id ? obj.original_condition_id : "";
  //     this.alternate_condition_id = obj.alternate_condition_id ? obj.alternate_condition_id : "";
  //     this.equivalent_level_multiplier = obj.equivalent_level_multiplier !== undefined ? obj.equivalent_level_multiplier : 1;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      original_condition_id: this.original_condition_id,
      alternate_condition_id: this.alternate_condition_id,
      equivalent_level_multiplier: this.equivalent_level_multiplier
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof AlternateConditionFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: AlternateConditionFeature) {
    this.original_condition_id = copyMe.original_condition_id;
    this.alternate_condition_id = copyMe.alternate_condition_id;
    this.equivalent_level_multiplier = copyMe.equivalent_level_multiplier;
  }

  clone(drop_ids = false): AlternateConditionFeature {
    const new_obj = new AlternateConditionFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class StartEquipmentFeature extends FloatingFeature {
  start_equipment: StartEquipmentChoice[] = [];

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Start Equipment";
    if (obj) {
      this.start_equipment = [];
      if (obj.start_equipment) {
        // if (this.name.includes("Start Equipment - Paladin")) {
        //   debugger;
        // }
        for (const o of obj.start_equipment) {
          const choice = new StartEquipmentChoice(o);
          this.start_equipment.push(choice);
        }
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.start_equipment = [];
  //     if (obj.start_equipment) {
  //       obj.start_equipment.forEach((o: any) => {
  //         const choice = new StartEquipmentChoice(o);
  //         this.start_equipment.push(choice);
  //       });
  //     }
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      start_equipment: this.start_equipment.map(o => o.toDBObj())
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof StartEquipmentFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: StartEquipmentFeature) {
    this.start_equipment = copyMe.start_equipment.map(o => o.clone());
  }

  clone(drop_ids = false): StartEquipmentFeature {
    const new_obj = new StartEquipmentFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class UnarmedStrikesFeature extends FloatingFeature {
  bonus_action_attack = false;
  add_attack_mod = false;
  use_ability_score = "STR";
  dts: DamageType[] = ["Bludgeoning"];
  damage_rolls: RollPlusUpgradable = new RollPlusUpgradable();

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Unarmed Strikes";
    if (obj) {
      this.bonus_action_attack = obj.bonus_action_attack ? obj.bonus_action_attack : false;
      this.add_attack_mod = obj.add_attack_mod ? obj.add_attack_mod : false;
      this.use_ability_score = obj.use_ability_score ? obj.use_ability_score : "STR";
      this.dts = obj.dts ? obj.dts : ["Bludgeoning"];
      if (obj.dts === undefined && obj.damage_type) {
        if (isDamageType(obj.damage_type)) {
          this.dts = [obj.damage_type];
        }
      }
      this.damage_rolls = obj.damage_rolls ? new RollPlusUpgradable(obj.damage_rolls) : new RollPlusUpgradable();
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.bonus_action_attack = obj.bonus_action_attack ? obj.bonus_action_attack : false;
  //     this.add_attack_mod = obj.add_attack_mod ? obj.add_attack_mod : false;
  //     this.use_ability_score = obj.use_ability_score ? obj.use_ability_score : "STR";
  //     this.dts = obj.dts ? obj.dts : ["Bludgeoning"];
  //     if (obj.dts === undefined && obj.damage_type) {
  //       if (isDamageType(obj.damage_type)) {
  //         this.dts = [obj.damage_type];
  //       }
  //     }
  //     this.damage_rolls = obj.damage_rolls ? new RollPlusUpgradable(obj.damage_rolls) : new RollPlusUpgradable();
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      bonus_action_attack: this.bonus_action_attack,
      add_attack_mod: this.add_attack_mod,
      use_ability_score: this.use_ability_score,
      dts: this.dts,
      damage_rolls: this.damage_rolls.toDBObj()
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof UnarmedStrikesFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: UnarmedStrikesFeature) {
    this.bonus_action_attack = copyMe.bonus_action_attack;
    this.add_attack_mod = copyMe.add_attack_mod;
    this.use_ability_score = copyMe.use_ability_score;
    this.dts = [...copyMe.dts];
    this.damage_rolls = copyMe.damage_rolls;
  }

  copyFromModifier(copyMe: Modifier) {
    super.copy(copyMe);
    this.feature_type = "Unarmed Strikes";
    this.bonus_action_attack = copyMe.multiply;
    this.use_ability_score = copyMe.modifies_detail_3;
    this.add_attack_mod = copyMe.replace_asm;
    if (isDamageType(copyMe.modifies_detail_2)) {
      this.dts = [copyMe.modifies_detail_2];
    }
    this.damage_rolls = new RollPlusUpgradable();
    if (copyMe.replace_asm) {
      this.damage_rolls.ability_score = this.use_ability_score;
    }
    this.damage_rolls.count = copyMe.upgradable_number_array[0];
    this.damage_rolls.size = copyMe.upgradable_number_array[1];
  }

  clone(drop_ids = false): UnarmedStrikesFeature {
    const new_obj = new UnarmedStrikesFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// This type is for features that let you change what ability score to use for your attacks
export class ReplaceAbilityScoreFeature extends FloatingFeature {
  use_ability_score: AbilityScoreExpandedType = "STR";
  magical_weapons_only = false;
  weapon_keyword_ids: string[] = [];
  game_class_id = "";

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Replace Ability Score for Attacks";
    if (obj) {
      this.use_ability_score = obj.use_ability_score ? obj.use_ability_score : "STR";
      this.magical_weapons_only = obj.magical_weapons_only ? obj.magical_weapons_only : false;
      this.weapon_keyword_ids = obj.weapon_keyword_ids ? obj.weapon_keyword_ids : [];
      this.game_class_id = obj.game_class_id ? obj.game_class_id : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.use_ability_score = obj.use_ability_score ? obj.use_ability_score : "STR";
  //     this.magical_weapons_only = obj.magical_weapons_only ? obj.magical_weapons_only : false;
  //     this.weapon_keyword_ids = obj.weapon_keyword_ids ? obj.weapon_keyword_ids : [];
  //     this.game_class_id = obj.game_class_id ? obj.game_class_id : "";
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      use_ability_score: this.use_ability_score,
      magical_weapons_only: this.magical_weapons_only,
      weapon_keyword_ids: this.weapon_keyword_ids,
      game_class_id: this.game_class_id
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof ReplaceAbilityScoreFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: ReplaceAbilityScoreFeature) {
    this.use_ability_score = copyMe.use_ability_score;
    this.magical_weapons_only = copyMe.magical_weapons_only;
    this.weapon_keyword_ids = [...copyMe.weapon_keyword_ids];
    this.game_class_id = copyMe.game_class_id;
  }

  copyFromModifier(copyMe: Modifier) {
    super.copy(copyMe);
    this.feature_type = "Replace Ability Score for Attacks";
    if (isStatType(copyMe.modifies_detail_3)) {
      this.use_ability_score = copyMe.modifies_detail_3;
    }
  }

  clone(drop_ids = false): ReplaceAbilityScoreFeature {
    const new_obj = new ReplaceAbilityScoreFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// This type is for adding extra attack types to weapons for things like SS and GWM
export class AttackUpgradeFeature extends FloatingFeature {
  weapon_limit: WeaponLimitation = new WeaponLimitation();
  attack_modifier: UpgradableNumber = new UpgradableNumber();
  damage_modifier: UpgradableNumber = new UpgradableNumber();
  include_ability_mod = true;

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Attack Upgrade";
    if (obj) {
      if (obj.weapon_limit) {
        this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
      } else {
        this.weapon_limit = new WeaponLimitation();
        this.weapon_limit.weapon_keyword_ids = obj.weapon_keyword_ids ? obj.weapon_keyword_ids : [];
        const item_ids: string[] = obj.weapon_ids ? obj.weapon_ids : [];
        this.weapon_limit.pseudo_item_ids = item_ids.map(o => `B:${o}`);
        this.weapon_limit.attack_types = obj.attack_types ? obj.attack_types : [];
        this.weapon_limit.damage_types = obj.damage_types ? obj.damage_types : [];
      }
      this.attack_modifier = obj.attack_modifier ? new UpgradableNumber(obj.attack_modifier) : new UpgradableNumber();
      this.damage_modifier = obj.damage_modifier ? new UpgradableNumber(obj.damage_modifier) : new UpgradableNumber();
      this.include_ability_mod = obj.include_ability_mod !== undefined ? obj.include_ability_mod : true;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     if (obj.weapon_limit) {
  //       this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
  //     } else {
  //       this.weapon_limit = new WeaponLimitation();
  //       this.weapon_limit.weapon_keyword_ids = obj.weapon_keyword_ids ? obj.weapon_keyword_ids : [];
  //       const item_ids: string[] = obj.weapon_ids ? obj.weapon_ids : [];
  //       this.weapon_limit.pseudo_item_ids = item_ids.map(o => `B:${o}`);
  //       this.weapon_limit.attack_types = obj.attack_types ? obj.attack_types : [];
  //       this.weapon_limit.damage_types = obj.damage_types ? obj.damage_types : [];
  //     }
  //     this.attack_modifier = obj.attack_modifier ? new UpgradableNumber(obj.attack_modifier) : new UpgradableNumber();
  //     this.damage_modifier = obj.damage_modifier ? new UpgradableNumber(obj.damage_modifier) : new UpgradableNumber();
  //     this.include_ability_mod = obj.include_ability_mod !== undefined ? obj.include_ability_mod : true;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      weapon_limit: this.weapon_limit.toDBObj(),
      attack_modifier: this.attack_modifier.toDBObj(),
      damage_modifier: this.damage_modifier.toDBObj(),
      include_ability_mod: this.include_ability_mod
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof AttackUpgradeFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: AttackUpgradeFeature) {
    this.weapon_limit = copyMe.weapon_limit.clone();
    this.attack_modifier = copyMe.attack_modifier.clone();
    this.damage_modifier = copyMe.damage_modifier.clone();
    this.include_ability_mod = copyMe.include_ability_mod;
  }

  clone(drop_ids = false): AttackUpgradeFeature {
    const new_obj = new AttackUpgradeFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * This allows characters to choose weapons that fit the limit, 
 * up to the count of the feature. 
 * 
 * Doing so gives the character access to the default weapon mastery property of that 
 * weapon type when using that weapon type.
 */
export class WeaponMasteryFeature extends FloatingFeature {
  count: UpgradableNumber = new UpgradableNumber(2);
  weapon_limit: WeaponLimitation = new WeaponLimitation();
  // improvement_type: "Change" | "Merge" | "None" = "None"; 

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Weapon Mastery";
    if (obj) {
      this.count = obj.count ? new UpgradableNumber(obj.count) : new UpgradableNumber(2);
      this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
      // this.improvement_type = obj.improvement_type ? obj.improvement_type : "None";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.count = obj.count ? new UpgradableNumber(obj.count) : new UpgradableNumber(2);
  //     this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
  //     // this.improvement_type = obj.improvement_type ? obj.improvement_type : "None";
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      count: this.count.toDBObj(),
      weapon_limit: this.weapon_limit.toDBObj(),
      // improvement_type: this.improvement_type
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof WeaponMasteryFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: WeaponMasteryFeature) {
    this.count = copyMe.count.clone();
    this.weapon_limit = copyMe.weapon_limit.clone();
    // this.improvement_type = copyMe.improvement_type;
  }

  clone(drop_ids = false): WeaponMasteryFeature {
    const new_obj = new WeaponMasteryFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * This one automatically gives you the associated Weapon Mastery Properties on weapons that fit the limit.
 * This means that each time you attack with such a weapon, you can apply any of the properties to the attack, 
 * plus whatever it already had, chosen at the time of the attack.
 * 
 * I'm adding it in for a new Fighter Feature. 
 * It frequently gives different weapon mastery properties than the default one 
 * of the weapon itself.
 */
export class WeaponMasteryMasterFeature extends FloatingFeature {
  weapon_limit: WeaponLimitation = new WeaponLimitation();
  wmp_ids: string[] = [];

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Weapon Mastery Master";
    if (obj) {
      this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
      this.wmp_ids = obj.wmp_ids ? obj.wmp_ids : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
  //     this.wmp_ids = obj.wmp_ids ? obj.wmp_ids : [];
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      weapon_limit: this.weapon_limit.toDBObj(),
      wmp_ids: this.wmp_ids
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof WeaponMasteryMasterFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: WeaponMasteryMasterFeature) {
    this.weapon_limit = copyMe.weapon_limit.clone();
    this.wmp_ids = [...copyMe.wmp_ids];
  }

  clone(drop_ids = false): WeaponMasteryMasterFeature {
    const new_obj = new WeaponMasteryMasterFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class ConsumedResource {
  resource_consumed = "None"; // Slot-X, Ki, Lay on Hands, Charge, etc.
  // _id of the source of the ResourceFeature giving resource_consumed,
  // if it's different from the source of the feature consuming it.
  resource_source_id = "";
  amount_consumed = 0;
  slot_level: SpellLevel = 1; // If it consumes slots then this is the minimum level of the slot
  slot_type = ""; // If it consumes a specific type of slot (usually Pact) then this holds that
  special_resource_amount: UpgradableNumber = new UpgradableNumber();
  special_resource_refresh_rule = ""; // Short Rest, Long Rest, Dawn, 1 Hour, 8 Hours, 24 Hours
  recharge_die: 4 | 6 | 8 | 10 | 12 | 20 | 100 = 6;
  recharge_on: number[] = [];
  
  constructor(obj?: any) {
    if (obj) {
      this.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "";
      this.resource_source_id = obj.resource_source_id ? obj.resource_source_id : "";
      this.amount_consumed = obj.amount_consumed ? obj.amount_consumed : 0;
      this.slot_level = obj.slot_level ? obj.slot_level : 1;
      this.slot_type = obj.slot_type ? obj.slot_type : "";
      this.special_resource_amount = obj.special_resource_amount ? new UpgradableNumber(obj.special_resource_amount) : new UpgradableNumber();
      this.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
      this.recharge_die = obj.recharge_die ? obj.recharge_die : 6;
      this.recharge_on = obj.recharge_on ? obj.recharge_on : [];
    }
  }

  toDBObj(): any {
    return {
      resource_consumed: this.resource_consumed,
      resource_source_id: this.resource_source_id,
      amount_consumed: this.amount_consumed,
      slot_level: this.slot_level,
      slot_type: this.slot_type,
      special_resource_amount: this.special_resource_amount.toDBObj(),
      special_resource_refresh_rule: this.special_resource_refresh_rule,
      recharge_die: this.recharge_die,
      recharge_on: this.recharge_on
    };
  }

  copy(copyMe: ConsumedResource): void {
    this.resource_consumed = copyMe.resource_consumed;
    this.resource_source_id = copyMe.resource_source_id;
    this.amount_consumed = copyMe.amount_consumed;
    this.slot_level = copyMe.slot_level;
    this.slot_type = copyMe.slot_type;
    this.special_resource_amount = copyMe.special_resource_amount.clone();
    this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    this.recharge_die = copyMe.recharge_die;
    this.recharge_on = [...copyMe.recharge_on];
  }

  clone(): ConsumedResource {
    const new_obj = new ConsumedResource();
    new_obj.copy(this);
    return new_obj;
  }
}

/**
 * This type is similar to AttackUpgradeFeature, 
 * but it's not a separate kind of attack, 
 * and it's just about adding more damage rolls, 
 * and they're able to be different damage types.
 * This will be used by Sneak Attack, Flame Tongue, etc.
 * Currently a lot of these kinds of features are there own abilities, 
 * but if it doesn't consume anything and is something that just happens on a hit
 * then it should be one of these instead.
 * 
 * Changing it into ExtraEffectsFeature, 
 * and instead of just having damage rolls,
 * it will have more ability effects.
 */
export class ExtraEffectsFeature extends FloatingFeature {
  apply_to: ("Weapons" | "Abilities" | "Spells")[] = [];
  weapon_limit: WeaponLimitation = new WeaponLimitation();
  ability_limit: AbilityLimitation = new AbilityLimitation();
  spell_limit: SpellLimitation = new SpellLimitation();
  potence_type: PotenceType = "None"; // Character, Class, or None
  potence_class = "";
  effects: AbilityEffect[] = [];
  show_range = false;
  save_dc: UpgradableNumber = new UpgradableNumber();
  attack_bonus: UpgradableNumber = new UpgradableNumber();

  consumed_resource: ConsumedResource = new ConsumedResource();
  
  time_restriction: TimeRestriction = "Every Time"; // Once per turn, Once on your turn, etc
  happens_when: HappensWhen = "Special";
  extra_restriction = "";

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Extra Effects";
    if (obj) {
      // if (obj.name === "Suffocate") {
      //   debugger;
      // }
      this.apply_to = obj.apply_to ? obj.apply_to : ["Weapons"];
      if (obj.weapon_limit) {
        this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
      }
      if (obj.ability_limit) {
        this.ability_limit = new AbilityLimitation(obj.ability_limit);
      }
      if (obj.spell_limit) {
        this.spell_limit = new SpellLimitation(obj.spell_limit);
      }
      this.potence_type = obj.potence_type ? obj.potence_type : "None";
      this.potence_class = obj.potence_class ? obj.potence_class : "";
      
      const saving_throw_ability_score = obj.saving_throw_ability_score ? obj.saving_throw_ability_score : "";
      this.effects = [];
      if (obj.effects && obj.effects.length) {
        obj.effects.forEach((effect: any) => {
          const new_effect = new AbilityEffect(effect);
          if (saving_throw_ability_score && !effect.saving_throw_ability_score) {
            new_effect.saving_throw_ability_score = saving_throw_ability_score;
          }
          this.effects.push(new_effect);
        });
      } else if (obj.potences) {
        const effect = new AbilityEffect();
        effect.potence_type = this.potence_type;
        effect.potence_class = this.potence_class;
        effect.type = "Damage";
        effect.damage_types.push("Typeless");
        effect.potences = obj.potences.map((p: any) => new Potence(p));
        this.effects.push(effect);
      }
      this.show_range = obj.show_range ? obj.show_range : false;
      this.save_dc = obj.save_dc ? new UpgradableNumber(obj.save_dc) : new UpgradableNumber();
      this.attack_bonus = obj.attack_bonus ? new UpgradableNumber(obj.attack_bonus) : new UpgradableNumber();
      
      this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
      if (!obj.consumed_resource) {
        this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "";
        this.consumed_resource.amount_consumed = obj.amount_consumed ? obj.amount_consumed : 0;
        this.consumed_resource.slot_level = obj.slot_level ? obj.slot_level : 1;
        this.consumed_resource.slot_type = obj.slot_type ? obj.slot_type : "";
        this.consumed_resource.special_resource_amount = obj.special_resource_amount ? new UpgradableNumber(obj.special_resource_amount) : new UpgradableNumber();
        this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
      }
      this.time_restriction = obj.time_restriction && TIME_RESTRICTIONS.includes(obj.time_restriction) ? obj.time_restriction : "Every Time";
      this.happens_when = obj.happens_when ? obj.happens_when : "Special";
      if (this.happens_when === "Special") {
        const desc = this.description.toLocaleLowerCase();
        if (desc.includes("on a hit") || desc.includes("on your attack roll") || desc.includes(" you hit")) {
          this.happens_when = "On a Hit";
        } else if (desc.includes("fail") || desc.includes("saving throw")) {
          this.happens_when = "On a Failed Save";
        } else if (desc.includes("when you cast ") || desc.includes("spells you cast")) {
          this.happens_when = "On Use";
        }
      }
      if (this.damage_types.includes("Typeless")) {
        const desc = this.description.toLocaleLowerCase();
        if (desc.includes("same type")) {
          for (const effect of this.effects) {
            if (effect.damage_types.includes("Typeless")) {
              effect.damage_types = effect.damage_types.filter(o => o !== "Typeless");
              effect.damage_types.push("Same as Main");
            }
          }
        }
      }
      this.extra_restriction = obj.extra_restriction ? obj.extra_restriction : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     // if (obj.name === "Suffocate") {
  //     //   debugger;
  //     // }
  //     this.apply_to = obj.apply_to ? obj.apply_to : ["Weapons"];
  //     if (obj.weapon_limit) {
  //       this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
  //     }
  //     if (obj.ability_limit) {
  //       this.ability_limit = new AbilityLimitation(obj.ability_limit);
  //     }
  //     if (obj.spell_limit) {
  //       this.spell_limit = new SpellLimitation(obj.spell_limit);
  //     }
  //     this.potence_type = obj.potence_type ? obj.potence_type : "None";
  //     this.potence_class = obj.potence_class ? obj.potence_class : "";
  //     // this.potences = obj.potences && obj.potences.length ? 
  //     //   obj.potences.map((p: any) => new Potence(p)) : [];
  //     const saving_throw_ability_score = obj.saving_throw_ability_score ? obj.saving_throw_ability_score : "";
  //     this.effects = [];
  //     if (obj.effects && obj.effects.length) {
  //       obj.effects.forEach((effect: any) => {
  //         const new_effect = new AbilityEffect(effect);
  //         if (saving_throw_ability_score && !effect.saving_throw_ability_score) {
  //           new_effect.saving_throw_ability_score = saving_throw_ability_score;
  //         }
  //         this.effects.push(new_effect);
  //       });
  //     } else if (obj.potences) {
  //       const effect = new AbilityEffect();
  //       effect.potence_type = this.potence_type;
  //       effect.potence_class = this.potence_class;
  //       effect.type = "Damage";
  //       effect.damage_types.push("Typeless");
  //       effect.potences = obj.potences.map((p: any) => new Potence(p));
  //       this.effects.push(effect);
  //     }
  //     this.show_range = obj.show_range ? obj.show_range : false;
  //     this.save_dc = obj.save_dc ? new UpgradableNumber(obj.save_dc) : new UpgradableNumber();
  //     this.attack_bonus = obj.attack_bonus ? new UpgradableNumber(obj.attack_bonus) : new UpgradableNumber();
      
  //     this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
  //     if (!obj.consumed_resource) {
  //       this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "";
  //       this.consumed_resource.amount_consumed = obj.amount_consumed ? obj.amount_consumed : 0;
  //       this.consumed_resource.slot_level = obj.slot_level ? obj.slot_level : 1;
  //       this.consumed_resource.slot_type = obj.slot_type ? obj.slot_type : "";
  //       this.consumed_resource.special_resource_amount = obj.special_resource_amount ? new UpgradableNumber(obj.special_resource_amount) : new UpgradableNumber();
  //       this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
  //     }
  //     this.time_restriction = obj.time_restriction && TIME_RESTRICTIONS.includes(obj.time_restriction) ? obj.time_restriction : "Every Time";
  //     this.happens_when = obj.happens_when ? obj.happens_when : "Special";
  //     if (this.happens_when === "Special") {
  //       const desc = this.description.toLocaleLowerCase();
  //       if (desc.includes("on a hit") || desc.includes("on your attack roll") || desc.includes(" you hit")) {
  //         this.happens_when = "On a Hit";
  //       } else if (desc.includes("fail") || desc.includes("saving throw")) {
  //         this.happens_when = "On a Failed Save";
  //       } else if (desc.includes("when you cast ") || desc.includes("spells you cast")) {
  //         this.happens_when = "On Use";
  //       }
  //     }
  //     if (this.damage_types.includes("Typeless")) {
  //       const desc = this.description.toLocaleLowerCase();
  //       if (desc.includes("same type")) {
  //         for (const effect of this.effects) {
  //           if (effect.damage_types.includes("Typeless")) {
  //             effect.damage_types = effect.damage_types.filter(o => o !== "Typeless");
  //             effect.damage_types.push("Same as Main");
  //           }
  //         }
  //       }
  //     }
  //     this.extra_restriction = obj.extra_restriction ? obj.extra_restriction : "";
  //     // if (this.description.includes("half as much")) {
  //     //   for (const effect of this.effects) {
  //     //     if (effect.type === "Damage") {
  //     //       effect.half_on_success = true;
  //     //     }
  //     //   }
  //     // }
  //   }
  // }
  
  get the_effect_types() {
    return this.effects.map(o => o.type);
  }
  
  get damage_types() {
    return this.effects.flatMap(o => o.damage_types);
  }

  get the_attack_types(): AttackType[] {
    return this.effects.map(o => o.attack_type);
  }

  get the_utility_details(): string[] {
    return this.effects.filter(o => ["Control","Utility"].includes(o.type)).map(o => o.utility_detail);
  }

  get ee_type(): string {
    return this.use_name;
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    // debugger;
    return {
      ...db_obj,
      apply_to: this.apply_to,
      weapon_limit: this.weapon_limit.toDBObj(),
      ability_limit: this.ability_limit.toDBObj(),
      spell_limit: this.spell_limit.toDBObj(),
      potence_type: this.potence_type,
      potence_class: this.potence_class,
      effects: this.effects.map(o => o.toDBObj()),
      // potences: this.potences.map(o => o.toDBObj()),
      show_range: this.show_range,
      // saving_throw_ability_score: this.saving_throw_ability_score,
      save_dc: this.save_dc.toDBObj(),
      attack_bonus: this.attack_bonus.toDBObj(),
      consumed_resource: this.consumed_resource.toDBObj(),
      // resource_consumed: this.resource_consumed,
      // amount_consumed: this.amount_consumed,
      // slot_level: this.slot_level,
      // slot_type: this.slot_type,
      // special_resource_amount: this.special_resource_amount.toDBObj(),
      // special_resource_refresh_rule: this.special_resource_refresh_rule,
      time_restriction: this.time_restriction,
      happens_when: this.happens_when,
      extra_restriction: this.extra_restriction,
    };
  }

  toLight(): any {
    const db_obj = super.toLight();
    // debugger;
    return {
      ...db_obj,
      apply_to: this.apply_to,
      weapon_limit: this.weapon_limit.toDBObj(),
      ability_limit: this.ability_limit.toDBObj(),
      spell_limit: this.spell_limit.toDBObj(),
      potence_type: this.potence_type,
      potence_class: this.potence_class,
      effects: this.effects.map(o => o.toLight()),
      show_range: this.show_range,
      save_dc: this.save_dc.toDBObj(),
      attack_bonus: this.attack_bonus.toDBObj(),
      consumed_resource: this.consumed_resource.toDBObj(),
      time_restriction: this.time_restriction,
      happens_when: this.happens_when,
      extra_restriction: this.extra_restriction,
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof ExtraEffectsFeature) {
      this.safe_copy(copyMe);
    } else if (copyMe instanceof ExtraEffectTypeFeature) {
      this.apply_to = [...copyMe.apply_to];
      this.weapon_limit = copyMe.weapon_limit.clone();
      this.ability_limit = copyMe.ability_limit.clone();
      this.spell_limit = copyMe.spell_limit.clone();
      this.potence_type = copyMe.potence_type;
      this.potence_class = copyMe.potence_class;
      this.show_range = copyMe.show_range;
      this.save_dc = copyMe.save_dc.clone();
      this.attack_bonus = copyMe.attack_bonus.clone();
      
      this.consumed_resource = copyMe.consumed_resource.clone();
      // this.resource_consumed = copyMe.resource_consumed;
      // this.amount_consumed = copyMe.amount_consumed;
      // this.slot_level = copyMe.slot_level;
      // this.slot_type = copyMe.slot_type;
      // this.special_resource_amount = copyMe.special_resource_amount.clone();
      // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
      
      this.time_restriction = copyMe.time_restriction;
      this.happens_when = copyMe.happens_when;
      this.extra_restriction = copyMe.extra_restriction;
    } else if (copyMe instanceof ExtraEffectsOfTypeFeature) {
      this.effects = copyMe.effects.map(o => o.clone());
    } else if (copyMe instanceof Ability) {
      this.effects = copyMe.effects.map(o => o.clone());
      this.show_range = true;
      this.save_dc = copyMe.dc.clone();
      this.attack_bonus = copyMe.attack_bonus.clone();
      
      this.consumed_resource = copyMe.consumed_resource.clone();
      // this.resource_consumed = copyMe.resource_consumed;
      // this.amount_consumed = copyMe.amount_consumed;
      // this.slot_level = copyMe.slot_level;
      // this.slot_type = copyMe.slot_type;
      // this.special_resource_amount = copyMe.special_resource_amount.clone();
      // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    } else if (copyMe instanceof SpellModifier || 
      copyMe instanceof SpellDamageOptions || 
      copyMe instanceof SpellsFromList || 
      copyMe instanceof ExtraEffectTypeFeature) {
      this.apply_to.push("Spells");
      this.spell_limit = copyMe.spell_limit.clone();
    }
  }

  safe_copy(copyMe: ExtraEffectsFeature) {
    this.apply_to = [...copyMe.apply_to];
    this.weapon_limit = copyMe.weapon_limit.clone();
    this.ability_limit = copyMe.ability_limit.clone();
    this.spell_limit = copyMe.spell_limit.clone();
    this.potence_type = copyMe.potence_type;
    this.potence_class = copyMe.potence_class;
    this.effects = copyMe.effects.map(o => o.clone());
    // this.potences = copyMe.potences.map(o => o.clone());
    this.show_range = copyMe.show_range;
    // this.saving_throw_ability_score = copyMe.saving_throw_ability_score;
    this.save_dc = copyMe.save_dc.clone();
    this.attack_bonus = copyMe.attack_bonus.clone();
    
    this.consumed_resource = copyMe.consumed_resource.clone();
    // this.resource_consumed = copyMe.resource_consumed;
    // this.amount_consumed = copyMe.amount_consumed;
    // this.slot_level = copyMe.slot_level;
    // this.slot_type = copyMe.slot_type;
    // this.special_resource_amount = copyMe.special_resource_amount.clone();
    // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    
    this.time_restriction = copyMe.time_restriction;
    this.happens_when = copyMe.happens_when;
    this.extra_restriction = copyMe.extra_restriction;
  }

  clone(drop_ids = false): ExtraEffectsFeature {
    const new_obj = new ExtraEffectsFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
/**
 * This is much akin to the standard eef.  
 * eetf doesn't have any actual effects as part of it though.
 * Instead they're in a different feature type: ExtraEffectsOfTypeFeature (eeotf).
 * This allows me to specify a type, and then any eeotf's of that type will be gathered 
 * together by the eetf, and then displayed together like a standard eef.
 */
export class ExtraEffectTypeFeature extends FloatingFeature {
  apply_to: ("Weapons" | "Abilities" | "Spells")[] = [];
  weapon_limit: WeaponLimitation = new WeaponLimitation();
  ability_limit: AbilityLimitation = new AbilityLimitation();
  spell_limit: SpellLimitation = new SpellLimitation();
  potence_type: PotenceType = "None"; // Character, Class, or None
  potence_class = "";
  show_range = false;
  save_dc: UpgradableNumber = new UpgradableNumber();
  attack_bonus: UpgradableNumber = new UpgradableNumber();

  consumed_resource: ConsumedResource = new ConsumedResource();

  // resource_consumed: string = "None"; // Slot-X, Ki, Lay on Hands, Charge, etc.
  // amount_consumed: number = 0;
  // slot_level: SpellLevel = 1; // If it consumes slots then this is the minimum level of the slot
  // slot_type: string = ""; // If it consumes a specific type of slot (usually Pact) then this holds that
  // special_resource_amount: UpgradableNumber = new UpgradableNumber();
  // special_resource_refresh_rule: string = ""; // Short Rest, Long Rest, Dawn, 1 Hour, 8 Hours, 24 Hours

  time_restriction: TimeRestriction = "Every Time"; // Once per turn, Once on your turn, etc
  happens_when: HappensWhen = "Special";
  extra_restriction = "";
  ee_type = "";

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Extra Effect Type";
    if (obj) {
      this.apply_to = obj.apply_to ? obj.apply_to : ["Weapons"];
      if (obj.weapon_limit) {
        this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
      }
      if (obj.ability_limit) {
        this.ability_limit = new AbilityLimitation(obj.ability_limit);
      }
      if (obj.spell_limit) {
        this.spell_limit = new SpellLimitation(obj.spell_limit);
      }
      this.potence_type = obj.potence_type ? obj.potence_type : "None";
      this.potence_class = obj.potence_class ? obj.potence_class : "";
      this.show_range = obj.show_range ? obj.show_range : false;
      this.save_dc = obj.save_dc ? new UpgradableNumber(obj.save_dc) : new UpgradableNumber();
      
      this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
      if (!obj.consumed_resource) {
        this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "";
        this.consumed_resource.amount_consumed = obj.amount_consumed ? obj.amount_consumed : 0;
        this.consumed_resource.slot_level = obj.slot_level ? obj.slot_level : 1;
        this.consumed_resource.slot_type = obj.slot_type ? obj.slot_type : "";
        this.consumed_resource.special_resource_amount = obj.special_resource_amount ? new UpgradableNumber(obj.special_resource_amount) : new UpgradableNumber();
        this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
      }
      this.time_restriction = obj.time_restriction && TIME_RESTRICTIONS.includes(obj.time_restriction) ? obj.time_restriction : "Every Time";
      this.happens_when = obj.happens_when ? obj.happens_when : "Special";
      if (this.happens_when === "Special") {
        const desc = this.description.toLocaleLowerCase();
        if (desc.includes("on a hit") || desc.includes("on your attack roll") || desc.includes(" you hit")) {
          this.happens_when = "On a Hit";
        } else if (desc.includes("fail") || desc.includes("saving throw")) {
          this.happens_when = "On a Failed Save";
        } else if (desc.includes("when you cast ") || desc.includes("spells you cast")) {
          this.happens_when = "On Use";
        }
      }
      this.extra_restriction = obj.extra_restriction ? obj.extra_restriction : "";
      this.ee_type = obj.ee_type ? obj.ee_type : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.apply_to = obj.apply_to ? obj.apply_to : ["Weapons"];
  //     if (obj.weapon_limit) {
  //       this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
  //     }
  //     if (obj.ability_limit) {
  //       this.ability_limit = new AbilityLimitation(obj.ability_limit);
  //     }
  //     if (obj.spell_limit) {
  //       this.spell_limit = new SpellLimitation(obj.spell_limit);
  //     }
  //     this.potence_type = obj.potence_type ? obj.potence_type : "None";
  //     this.potence_class = obj.potence_class ? obj.potence_class : "";
  //     this.show_range = obj.show_range ? obj.show_range : false;
  //     this.save_dc = obj.save_dc ? new UpgradableNumber(obj.save_dc) : new UpgradableNumber();
      
  //     this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
  //     if (!obj.consumed_resource) {
  //       this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "";
  //       this.consumed_resource.amount_consumed = obj.amount_consumed ? obj.amount_consumed : 0;
  //       this.consumed_resource.slot_level = obj.slot_level ? obj.slot_level : 1;
  //       this.consumed_resource.slot_type = obj.slot_type ? obj.slot_type : "";
  //       this.consumed_resource.special_resource_amount = obj.special_resource_amount ? new UpgradableNumber(obj.special_resource_amount) : new UpgradableNumber();
  //       this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
  //     }
  //     this.time_restriction = obj.time_restriction && TIME_RESTRICTIONS.includes(obj.time_restriction) ? obj.time_restriction : "Every Time";
  //     this.happens_when = obj.happens_when ? obj.happens_when : "Special";
  //     if (this.happens_when === "Special") {
  //       const desc = this.description.toLocaleLowerCase();
  //       if (desc.includes("on a hit") || desc.includes("on your attack roll") || desc.includes(" you hit")) {
  //         this.happens_when = "On a Hit";
  //       } else if (desc.includes("fail") || desc.includes("saving throw")) {
  //         this.happens_when = "On a Failed Save";
  //       } else if (desc.includes("when you cast ") || desc.includes("spells you cast")) {
  //         this.happens_when = "On Use";
  //       }
  //     }
  //     this.extra_restriction = obj.extra_restriction ? obj.extra_restriction : "";
  //     this.ee_type = obj.ee_type ? obj.ee_type : "";
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      apply_to: this.apply_to,
      weapon_limit: this.weapon_limit.toDBObj(),
      ability_limit: this.ability_limit.toDBObj(),
      spell_limit: this.spell_limit.toDBObj(),
      potence_type: this.potence_type,
      potence_class: this.potence_class,
      show_range: this.show_range,
      save_dc: this.save_dc.toDBObj(),
      consumed_resource: this.consumed_resource.toDBObj(),
      // resource_consumed: this.resource_consumed,
      // amount_consumed: this.amount_consumed,
      // slot_level: this.slot_level,
      // slot_type: this.slot_type,
      // special_resource_amount: this.special_resource_amount.toDBObj(),
      // special_resource_refresh_rule: this.special_resource_refresh_rule,
      time_restriction: this.time_restriction,
      happens_when: this.happens_when,
      extra_restriction: this.extra_restriction,
      ee_type: this.ee_type
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof ExtraEffectTypeFeature || copyMe instanceof ExtraEffectsFeature) {
      this.safe_copy(copyMe);
    } else if (copyMe instanceof SpellModifier || 
      copyMe instanceof SpellDamageOptions || 
      copyMe instanceof SpellsFromList) {
      this.apply_to.push("Spells");
      this.spell_limit = copyMe.spell_limit.clone();
    }
  }

  safe_copy(copyMe: ExtraEffectTypeFeature | ExtraEffectsFeature) {
    this.apply_to = [...copyMe.apply_to];
    this.weapon_limit = copyMe.weapon_limit.clone();
    this.ability_limit = copyMe.ability_limit.clone();
    this.spell_limit = copyMe.spell_limit.clone();
    this.potence_type = copyMe.potence_type;
    this.potence_class = copyMe.potence_class;
    this.show_range = copyMe.show_range;
    this.save_dc = copyMe.save_dc.clone();
    this.attack_bonus = copyMe.attack_bonus.clone();
    
    this.consumed_resource = copyMe.consumed_resource.clone();
    // this.resource_consumed = copyMe.resource_consumed;
    // this.amount_consumed = copyMe.amount_consumed;
    // this.slot_level = copyMe.slot_level;
    // this.slot_type = copyMe.slot_type;
    // this.special_resource_amount = copyMe.special_resource_amount.clone();
    // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    
    this.time_restriction = copyMe.time_restriction;
    this.happens_when = copyMe.happens_when;
    this.extra_restriction = copyMe.extra_restriction;
    this.ee_type = copyMe.ee_type;
  }

  copyExtraEffectsFeature(copyMe: ExtraEffectsFeature) {
    this.apply_to = [...copyMe.apply_to];
    this.weapon_limit = copyMe.weapon_limit.clone();
    this.ability_limit = copyMe.ability_limit.clone();
    this.spell_limit = copyMe.spell_limit.clone();
    this.potence_type = copyMe.potence_type;
    this.potence_class = copyMe.potence_class;
    this.show_range = copyMe.show_range;
    this.save_dc = copyMe.save_dc.clone();
    this.attack_bonus = copyMe.attack_bonus.clone();
    
    this.consumed_resource = copyMe.consumed_resource.clone();
    // this.resource_consumed = copyMe.resource_consumed;
    // this.amount_consumed = copyMe.amount_consumed;
    // this.slot_level = copyMe.slot_level;
    // this.slot_type = copyMe.slot_type;
    // this.special_resource_amount = copyMe.special_resource_amount.clone();
    // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;

    this.time_restriction = copyMe.time_restriction;
    this.happens_when = copyMe.happens_when;
    this.extra_restriction = copyMe.extra_restriction;
  }

  clone(drop_ids = false): ExtraEffectTypeFeature {
    const new_obj = new ExtraEffectTypeFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class ExtraEffectsOfTypeFeature extends FloatingFeature {
  effects: AbilityEffect[] = [];
  ee_type = "";
  
  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Extra Effects of Type";
    if (obj) {
      this.effects = [];
      if (obj.effects && obj.effects.length) {
        obj.effects.forEach((effect: any) => {
          const new_effect = new AbilityEffect(effect);
          this.effects.push(new_effect);
        });
      }
      this.ee_type = obj.ee_type ? obj.ee_type : "";
      if (this.damage_types.includes("Typeless")) {
        const desc = this.description.toLocaleLowerCase();
        if (desc.includes("same type")) {
          for (const effect of this.effects) {
            if (effect.damage_types.includes("Typeless")) {
              effect.damage_types = effect.damage_types.filter(o => o !== "Typeless");
              effect.damage_types.push("Same as Main");
            }
          }
        }
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.effects = [];
  //     if (obj.effects && obj.effects.length) {
  //       obj.effects.forEach((effect: any) => {
  //         const new_effect = new AbilityEffect(effect);
  //         this.effects.push(new_effect);
  //       });
  //     }
  //     this.ee_type = obj.ee_type ? obj.ee_type : "";
  //     if (this.damage_types.includes("Typeless")) {
  //       const desc = this.description.toLocaleLowerCase();
  //       if (desc.includes("same type")) {
  //         for (const effect of this.effects) {
  //           if (effect.damage_types.includes("Typeless")) {
  //             effect.damage_types = effect.damage_types.filter(o => o !== "Typeless");
  //             effect.damage_types.push("Same as Main");
  //           }
  //         }
  //       }
  //     }
  //     // if (this.description.includes("half as much")) {
  //     //   for (const effect of this.effects) {
  //     //     if (effect.type === "Damage") {
  //     //       effect.half_on_success = true;
  //     //     }
  //     //   }
  //     // }
  //   }
  // }
  
  get the_effect_types() {
    return this.effects.map(o => o.type);
  }
  
  get damage_types() {
    return this.effects.flatMap(o => o.damage_types);
  }

  get the_attack_types(): AttackType[] {
    return this.effects.map(o => o.attack_type);
  }

  get the_utility_details(): string[] {
    return this.effects.filter(o => ["Control","Utility"].includes(o.type)).map(o => o.utility_detail);
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      effects: this.effects.map(o => o.toDBObj()),
      ee_type: this.ee_type
    };
  }

  toLight(): any {
    const db_obj = super.toLight();
    return {
      ...db_obj,
      effects: this.effects.map(o => o.toLight()),
      ee_type: this.ee_type
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof ExtraEffectsOfTypeFeature || copyMe instanceof ExtraEffectsFeature) {
      this.safe_copy(copyMe);
    } else if (copyMe instanceof Ability) {
      this.effects = copyMe.effects.map(o => o.clone());
      // this.show_range = true;
      // this.save_dc = copyMe.dc.clone();
      
      // this.resource_consumed = copyMe.resource_consumed;
      // this.amount_consumed = copyMe.amount_consumed;
      // this.slot_level = copyMe.slot_level;
      // this.slot_type = copyMe.slot_type;
      // this.special_resource_amount = copyMe.special_resource_amount.clone();
      // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    }
  }

  safe_copy(copyMe: ExtraEffectsOfTypeFeature | ExtraEffectsFeature) {
    this.effects = copyMe.effects.map(o => o.clone());
    this.ee_type = copyMe.ee_type;
  }

  copyExtraEffectsFeature(copyMe: ExtraEffectsFeature) {
    this.effects = copyMe.effects.map(o => o.clone());
  }

  clone(drop_ids = false): ExtraEffectsOfTypeFeature {
    const new_obj = new ExtraEffectsOfTypeFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// This type is for making some weapons able to be used as bonus action attacks
// If it already could (ie via Two-Weapon Fighting), 
// then it will add attack mod if it couldn't before
export class BonusActionAttackFeature extends FloatingFeature {
  weapon_limit: WeaponLimitation = new WeaponLimitation();

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Bonus Action Attack";
    if (obj) {
      if (obj.weapon_limit) {
        this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
      } else {
        this.weapon_limit = new WeaponLimitation();
        this.weapon_limit.weapon_keyword_ids = obj.weapon_keyword_ids ? obj.weapon_keyword_ids : [];
        const item_ids: string[] = obj.weapon_ids ? obj.weapon_ids : [];
        this.weapon_limit.pseudo_item_ids = item_ids.map(o => `B:${o}`);
        this.weapon_limit.attack_types = obj.attack_types ? obj.attack_types : [];
        this.weapon_limit.damage_types = obj.damage_types ? obj.damage_types : [];
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     if (obj.weapon_limit) {
  //       this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
  //     } else {
  //       this.weapon_limit = new WeaponLimitation();
  //       this.weapon_limit.weapon_keyword_ids = obj.weapon_keyword_ids ? obj.weapon_keyword_ids : [];
  //       const item_ids: string[] = obj.weapon_ids ? obj.weapon_ids : [];
  //       this.weapon_limit.pseudo_item_ids = item_ids.map(o => `B:${o}`);
  //       this.weapon_limit.attack_types = obj.attack_types ? obj.attack_types : [];
  //       this.weapon_limit.damage_types = obj.damage_types ? obj.damage_types : [];
  //     }
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      weapon_limit: this.weapon_limit.toDBObj(),
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof BonusActionAttackFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: BonusActionAttackFeature) {
    this.weapon_limit = copyMe.weapon_limit.clone();
  }

  clone(drop_ids = false): BonusActionAttackFeature {
    const new_obj = new BonusActionAttackFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// This type is for making some Light Attacks 
// (Formerly Two Weapon Fighting BA Attacks) add the modifier to the damage roll
export class LightAttackAddDamageMod extends FloatingFeature {
  weapon_limit: WeaponLimitation = new WeaponLimitation();

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Light Attack Add Damage Mod";
    if (obj) {
      this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      weapon_limit: this.weapon_limit.toDBObj(),
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof LightAttackAddDamageMod) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: LightAttackAddDamageMod) {
    this.weapon_limit = copyMe.weapon_limit.clone();
  }

  clone(drop_ids = false): LightAttackAddDamageMod {
    const new_obj = new LightAttackAddDamageMod();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// This type is for making it treat weapon keywords as also being other weapon keywords
// If wk_ids is empty then it's just an upgrade of the weapon keyword itself.
export class WeaponKeywordUpgradeFeature extends FloatingFeature {
  base_wk_ids: string[] = [];
  excluded_wk_ids: string[] = [];
  added_wk_ids: string[] = [];
  range = 0;
  range2 = 0;

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Weapon Keyword Upgrade";
    if (obj) {
      this.base_wk_ids = obj.base_wk_ids ? obj.base_wk_ids : (obj.wk_id ? [obj.wk_id] : []);
      this.excluded_wk_ids = obj.excluded_wk_ids ? obj.excluded_wk_ids : [];
      this.added_wk_ids = obj.added_wk_ids ? obj.added_wk_ids : (obj.wk_ids ? obj.wk_ids : []);
      this.range = obj.range ? obj.range : 0;
      this.range2 = obj.range2 ? obj.range2 : 0;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.base_wk_ids = obj.base_wk_ids ? obj.base_wk_ids : (obj.wk_id ? [obj.wk_id] : []);
  //     this.excluded_wk_ids = obj.excluded_wk_ids ? obj.excluded_wk_ids : [];
  //     this.added_wk_ids = obj.added_wk_ids ? obj.added_wk_ids : (obj.wk_ids ? obj.wk_ids : []);
  //     this.range = obj.range ? obj.range : 0;
  //     this.range2 = obj.range2 ? obj.range2 : 0;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      base_wk_ids: this.base_wk_ids,
      excluded_wk_ids: this.excluded_wk_ids,
      added_wk_ids: this.added_wk_ids,
      range: this.range,
      range2: this.range2
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof WeaponKeywordUpgradeFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: WeaponKeywordUpgradeFeature) {
    this.base_wk_ids = [...copyMe.base_wk_ids];
    this.excluded_wk_ids = [...copyMe.excluded_wk_ids];
    this.added_wk_ids = [...copyMe.added_wk_ids];
    this.range = copyMe.range;
    this.range2 = copyMe.range2;
  }

  clone(drop_ids = false): WeaponKeywordUpgradeFeature {
    const new_obj = new WeaponKeywordUpgradeFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// Adds Weapon Keywords to Weapons of this type
export class WeaponKeywordUpgradeFeature2 extends FloatingFeature {
  weapon_type: WeaponType | "Selectable" = "None";
  selectable_key = "";
  added_wk_ids: string[] = [];
  range = 0;
  range2 = 0;

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Weapon Keyword Upgrade 2";
    if (obj) {
      this.weapon_type = obj.weapon_type ? obj.weapon_type : "None";
      this.selectable_key = obj.selectable_key ? obj.selectable_key : "";
      this.added_wk_ids = obj.added_wk_ids ? obj.added_wk_ids : [];
      this.range = obj.range ? obj.range : 0;
      this.range2 = obj.range2 ? obj.range2 : 0;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.weapon_type = obj.weapon_type ? obj.weapon_type : "None";
  //     this.selectable_key = obj.selectable_key ? obj.selectable_key : "";
  //     this.added_wk_ids = obj.added_wk_ids ? obj.added_wk_ids : [];
  //     this.range = obj.range ? obj.range : 0;
  //     this.range2 = obj.range2 ? obj.range2 : 0;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      weapon_type: this.weapon_type,
      selectable_key: this.selectable_key,
      added_wk_ids: this.added_wk_ids,
      range: this.range,
      range2: this.range2
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof WeaponKeywordUpgradeFeature2) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: WeaponKeywordUpgradeFeature2) {
    this.weapon_type = copyMe.weapon_type;
    this.selectable_key = copyMe.selectable_key;
    this.added_wk_ids = [...copyMe.added_wk_ids];
    this.range = copyMe.range;
    this.range2 = copyMe.range2;
  }

  clone(drop_ids = false): WeaponKeywordUpgradeFeature2 {
    const new_obj = new WeaponKeywordUpgradeFeature2();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// This type is for Monk Martial Arts upgrading weapon damage.
export class WeaponDamageUpgradeFeature extends FloatingFeature {
  weapon_limit: WeaponLimitation = new WeaponLimitation();
  damage_rolls: RollPlusUpgradable = new RollPlusUpgradable();

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Weapon Damage Upgrade";
    if (obj) {
      this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
      this.damage_rolls = obj.damage_rolls ? new RollPlusUpgradable(obj.damage_rolls) : new RollPlusUpgradable();
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.weapon_limit = obj.weapon_limit ? new WeaponLimitation(obj.weapon_limit) : new WeaponLimitation();
  //     this.damage_rolls = obj.damage_rolls ? new RollPlusUpgradable(obj.damage_rolls) : new RollPlusUpgradable();
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      weapon_limit: this.weapon_limit.toDBObj(),
      damage_rolls: this.damage_rolls.toDBObj()
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof WeaponDamageUpgradeFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: WeaponDamageUpgradeFeature) {
    this.weapon_limit = copyMe.weapon_limit.clone();
    this.damage_rolls = copyMe.damage_rolls.clone();
  }

  clone(drop_ids = false): WeaponDamageUpgradeFeature {
    const new_obj = new WeaponDamageUpgradeFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class CharacteristicFeature extends FloatingFeature {
  header1 = "";
  header2 = "";
  rollable_table_id = "New";
  show_in_source_name = false;
  replace_source_name = false;

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Characteristic";
    if (obj) {
      this.header1 = obj.header1 ? obj.header1 : "";
      this.header2 = obj.header2 ? obj.header2 : "";
      this.rollable_table_id = obj.rollable_table_id ? obj.rollable_table_id : "New";
      this.show_in_source_name = obj.show_in_source_name ? obj.show_in_source_name : false;
      this.replace_source_name = obj.replace_source_name ? obj.replace_source_name : false;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.header1 = obj.header1 ? obj.header1 : "";
  //     this.header2 = obj.header2 ? obj.header2 : "";
  //     this.rollable_table_id = obj.rollable_table_id ? obj.rollable_table_id : "New";
  //     this.show_in_source_name = obj.show_in_source_name ? obj.show_in_source_name : false;
  //     this.replace_source_name = obj.replace_source_name ? obj.replace_source_name : false;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      header1: this.header1,
      header2: this.header2,
      rollable_table_id: this.rollable_table_id,
      show_in_source_name: this.show_in_source_name,
      replace_source_name: this.replace_source_name
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof CharacteristicFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: CharacteristicFeature) {
    this.header1 = copyMe.header1;
    this.header2 = copyMe.header2;
    this.rollable_table_id = copyMe.rollable_table_id;
    this.show_in_source_name = copyMe.show_in_source_name;
    this.replace_source_name = copyMe.replace_source_name;
  }

  clone(drop_ids = false): CharacteristicFeature {
    const new_obj = new CharacteristicFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class MinimumRoll extends FloatingFeature {
  types: string[] = ["skill"];
  attack_types: string[] = [];
  ids: string[] = []; // ids of the skills if for specific skills
  proficiency_level = 0; // the required proficiency level for this to be applied
  threshold: UpgradableNumber = new UpgradableNumber(10); // the number that any die roll below it is treated as equal to it
  when = "";

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Minimum Roll";
    if (obj) {
      this.types = obj.types ? obj.types : ["skill"];
      this.attack_types = obj.attack_types ? obj.attack_types : [];
      this.ids = obj.ids ? obj.ids : [];
      this.proficiency_level = obj.proficiency_level ? obj.proficiency_level : 0;
      this.threshold = obj.threshold ? new UpgradableNumber(obj.threshold) : new UpgradableNumber(10);
      this.when = obj.when ? obj.when : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.types = obj.types ? obj.types : ["skill"];
  //     this.attack_types = obj.attack_types ? obj.attack_types : [];
  //     this.ids = obj.ids ? obj.ids : [];
  //     this.proficiency_level = obj.proficiency_level ? obj.proficiency_level : 0;
  //     this.threshold = obj.threshold ? new UpgradableNumber(obj.threshold) : new UpgradableNumber(10);
  //     this.when = obj.when ? obj.when : "";
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      types: this.types,
      attack_types: this.attack_types,
      ids: this.ids,
      proficiency_level: this.proficiency_level,
      threshold: this.threshold.toDBObj(),
      when: this.when
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof MinimumRoll) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: MinimumRoll) {
    this.types = [...copyMe.types];
    this.attack_types = [...copyMe.attack_types];
    this.ids = copyMe.ids;
    this.proficiency_level = copyMe.proficiency_level;
    this.threshold = copyMe.threshold.clone();
    this.when = copyMe.when;
  }

  clone(drop_ids = false): MinimumRoll {
    const new_obj = new MinimumRoll();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class SuggestedSpells extends FloatingFeature {
  suggestions: SuggestionsForLevel[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.suggestions = obj.suggestions ? obj.suggestions : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.suggestions = obj.suggestions ? obj.suggestions : [];
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      suggestions: this.suggestions
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SuggestedSpells) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: SuggestedSpells) {
    this.suggestions = [];
    copyMe.suggestions.forEach(sug => {
      this.suggestions.push({...sug});
    });
  }

  clone(drop_ids = false): SuggestedSpells {
    const new_obj = new SuggestedSpells();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export interface SuggestionsForLevel {
  level: number;
  suggestions: SuggestionsOfType[];
}

export interface SuggestionsOfType {
  type: DataType;
  ids: string[];
}

export class SubclassFeature extends FloatingFeature {
  subclasses_called = "Subclass";
  suggested = "";
  for_class_id = "";
  level = 1;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.subclasses_called = obj.subclasses_called ? obj.subclasses_called : "Subclass";
      this.suggested = obj.suggested ? obj.suggested : "";
      this.for_class_id = obj.for_class_id ? obj.for_class_id : "";
      this.level = obj.level ? obj.level : 1;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.subclasses_called = obj.subclasses_called ? obj.subclasses_called : "Subclass";
  //     this.suggested = obj.suggested ? obj.suggested : "";
  //     this.for_class_id = obj.for_class_id ? obj.for_class_id : "";
  //     this.level = obj.level ? obj.level : 1;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      subclasses_called: this.subclasses_called,
      suggested: this.suggested,
      for_class_id: this.for_class_id,
      level: this.level
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SubclassFeature) {
      this.safe_copy(copyMe);
    }
  }

  copyStringFeature(copyMe: StringFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.subclasses_called = copyMe.str;
  }

  safe_copy(copyMe: SubclassFeature) {
    this.subclasses_called = copyMe.subclasses_called;
    this.suggested = copyMe.suggested;
    this.for_class_id = copyMe.for_class_id;
    this.level = copyMe.level;
  }

  clone(drop_ids = false): SubclassFeature {
    const new_obj = new SubclassFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// This type is for adding extra attack types to weapons for things like SS and GWM
export class SummonUpgradeFeature extends FloatingFeature {
  bonus_hp: UpgradableNumber = new UpgradableNumber();
  per_hit_die = false;
  shared_res = false; // If this is true then you share all of your resistances and immunities with your summon.
  limits: CreatureLimitation = new CreatureLimitation();
  features: Feature[] = []; // Features to add to your summons
  // Actually, features can take care of this:
  // size_upgrade = new UpgradableNumber(); // This number gets added to the size_num of the summon, still constrained by size categories


  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Summon Upgrade";
    if (obj) {
      this.bonus_hp = obj.bonus_hp ? new UpgradableNumber(obj.bonus_hp) : new UpgradableNumber();
      this.per_hit_die = obj.per_hit_die ? obj.per_hit_die : false;
      this.shared_res = obj.shared_res ? obj.shared_res : false;
      this.limits = obj.limits ? new CreatureLimitation(obj.limits) : new CreatureLimitation();
      if (!obj.limits && obj.creature_types) {
        this.limits.creature_types = obj.creature_types as string[];
      }
      this.features = obj.features ? obj.features.map((o: any) => new Feature(o)) : [];
      // this.size_upgrade = obj.size_upgrade ? new UpgradableNumber(obj.size_upgrade) : new UpgradableNumber();
    }
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      bonus_hp: this.bonus_hp.toDBObj(),
      per_hit_die: this.per_hit_die,
      shared_res: this.shared_res,
      limits: this.limits.toDBObj(),
      features: this.features.map(o => o.toDBObj()),
      // size_upgrade: this.size_upgrade.toDBObj()
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SummonUpgradeFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: SummonUpgradeFeature) {
    this.bonus_hp = copyMe.bonus_hp.clone();
    this.per_hit_die = copyMe.per_hit_die;
    this.shared_res = copyMe.shared_res;
    this.limits = copyMe.limits.clone();
    this.features = copyMe.features.map(o => o.clone());
    // this.size_upgrade = copyMe.size_upgrade.clone();
  }

  clone(drop_ids = false): SummonUpgradeFeature {
    const new_obj = new SummonUpgradeFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// Used to make non-standard Action Types usable as Bonus Action or Reaction
export class ActionTypeUpgrade extends FloatingFeature {
  action_types: string[] = [];
  reaction = false;

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Action Type Upgrade";
    if (obj) {
      this.action_types = obj.action_types ? obj.action_types : [];
      this.reaction = obj.reaction ? obj.reaction : false;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.action_types = obj.action_types ? obj.action_types : [];
  //     this.reaction = obj.reaction ? obj.reaction : false;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      action_types: this.action_types,
      reaction: this.reaction
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof ActionTypeUpgrade) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: ActionTypeUpgrade) {
    this.action_types = copyMe.action_types;
    this.reaction = copyMe.reaction;
  }

  clone(drop_ids = false): ActionTypeUpgrade {
    const new_obj = new ActionTypeUpgrade();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// Used to make non-standard Action Types usable as Bonus Action or Reaction
export class SkillAsAbility extends FloatingFeature {
  skill_ids: string[] = [];
  casting_time: CastingTime = "A";
  special_casting_time = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.skill_ids = obj.skill_ids ? obj.skill_ids : (obj.skill_id ? [obj.skill_id] : []);
      this.casting_time = obj.casting_time ? obj.casting_time : "A";
      this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.skill_ids = obj.skill_ids ? obj.skill_ids : (obj.skill_id ? [obj.skill_id] : []);
  //     this.casting_time = obj.casting_time ? obj.casting_time : "A";
  //     this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
  //   }
  // }

  get resource_consumed() {
    return "";
  }

  get special_resource_refresh_rule() {
    return "";
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      skill_ids: this.skill_ids,
      casting_time: this.casting_time,
      special_casting_time: this.special_casting_time
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SkillAsAbility) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: SkillAsAbility) {
    this.skill_ids = copyMe.skill_ids;
    this.casting_time = copyMe.casting_time;
    this.special_casting_time = copyMe.special_casting_time;
  }

  clone(drop_ids = false): SkillAsAbility {
    const new_obj = new SkillAsAbility();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * Changing this so it can work for Magic Item Plans for the new Artificer.
 * 
 * limitations needs to be changed to be based on Potence.
 * count needs to be changed to a UGN.
 */
export class Selectable extends FloatingFeature {
  type: SelectableType = "Ability Score"; // Ability Score, Damage Type, School of Magic, Strings (lets the user supply them when defining the floating feature)
  the_key = "";
  options: string[] = [];
  show_in_source_name = false;
  limitations: Limitations = new Limitations();
  fs_specific = false;
  count: UpgradableNumber = new UpgradableNumber(1);

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.type = obj.type ? obj.type : "Ability Score";
      this.the_key = obj.the_key ? obj.the_key : "";
      this.options = obj.options ? [...obj.options] : [];
      this.show_in_source_name = obj.show_in_source_name ? obj.show_in_source_name : false;
      this.limitations = obj.limitations ? new Limitations(obj.limitations) : new Limitations();
      this.fs_specific = obj.fs_specific ? obj.fs_specific : false;
      this.count = obj.count !== undefined ? new UpgradableNumber(obj.count) : new UpgradableNumber(1);
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.type = obj.type ? obj.type : "Ability Score";
  //     this.the_key = obj.the_key ? obj.the_key : "";
  //     this.options = obj.options ? [...obj.options] : [];
  //     this.show_in_source_name = obj.show_in_source_name ? obj.show_in_source_name : false;
  //     this.limitations = obj.limitations ? new Limitations(obj.limitations) : new Limitations();
  //     this.fs_specific = obj.fs_specific ? obj.fs_specific : false;
  //     this.count = obj.count !== undefined ? new UpgradableNumber(obj.count) : new UpgradableNumber(1);
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      type: this.type,
      the_key: this.the_key,
      options: this.options,
      show_in_source_name: this.show_in_source_name,
      limitations: this.limitations.toDBObj(),
      fs_specific: this.fs_specific,
      count: this.count.toDBObj()
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof Selectable) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: Selectable) {
    this.type = copyMe.type;
    this.the_key = copyMe.the_key;
    this.options = [...copyMe.options];
    this.show_in_source_name = copyMe.show_in_source_name;
    this.limitations = copyMe.limitations.clone();
    this.fs_specific = copyMe.fs_specific;
    this.count = copyMe.count.clone();
  }

  clone(drop_ids = false): Selectable {
    const new_obj = new Selectable();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class SFFeature extends FloatingFeature {
  special_feature_type = "Any";
  special_feature_ids: string[] = []; // If empty then can be any, if only one, then that's it, otherwise choose one
  count: UpgradableNumber = new UpgradableNumber(1);
  show_in_source_name = false;
  allow_none = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.feature_type = "Special Feature";
      this.special_feature_type = obj.special_feature_type ? obj.special_feature_type : "Any";
      this.special_feature_ids = obj.special_feature_ids ? [...obj.special_feature_ids] : []; // If empty then can be any, if only one, then that's it, otherwise choose one
      this.count = obj.count ? new UpgradableNumber(obj.count) : new UpgradableNumber(1);
      this.show_in_source_name = obj.show_in_source_name ? obj.show_in_source_name : false;
      this.allow_none = obj.allow_none ? obj.allow_none : false;
    }
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      special_feature_type: this.special_feature_type,
      special_feature_ids: this.special_feature_ids,
      count: this.count.toDBObj(),
      show_in_source_name: this.show_in_source_name,
      allow_none: this.allow_none,
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SFFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: SFFeature) {
    this.special_feature_type = copyMe.special_feature_type;
    this.special_feature_ids = [...copyMe.special_feature_ids];
    this.count = copyMe.count.clone();
    this.show_in_source_name = copyMe.show_in_source_name;
    this.allow_none = copyMe.allow_none;
  }

  clone(drop_ids = false): SFFeature {
    const new_obj = new SFFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class SFMergeFeature extends FloatingFeature {
  special_feature_types: string[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.special_feature_types = obj.special_feature_types ? obj.special_feature_types : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.special_feature_types = obj.special_feature_types ? obj.special_feature_types : [];
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      special_feature_types: this.special_feature_types,
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SFMergeFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: SFMergeFeature) {
    this.special_feature_types = copyMe.special_feature_types;
  }

  clone(drop_ids = false): SFMergeFeature {
    const new_obj = new SFMergeFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class EldritchInvocationsFeature extends FloatingFeature {
  count: UpgradableNumber = new UpgradableNumber(1);
  ei_ids: string[] = [];

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Eldritch Invocations";
    if (obj) {
      this.count = obj.count ? new UpgradableNumber(obj.count) : new UpgradableNumber(1);
      this.ei_ids = obj.ei_ids ? obj.ei_ids : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.count = obj.count ? new UpgradableNumber(obj.count) : new UpgradableNumber(1);
  //     this.ei_ids = obj.ei_ids ? obj.ei_ids : [];
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      count: this.count.toDBObj(),
      ei_ids: this.ei_ids
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof EldritchInvocationsFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: EldritchInvocationsFeature) {
    this.count = copyMe.count.clone();
    this.ei_ids = [...copyMe.ei_ids];
  }

  clone(drop_ids = false): EldritchInvocationsFeature {
    const new_obj = new EldritchInvocationsFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class PactBoonFeature extends FloatingFeature {
  pb_ids: string[] = [];

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Pact Boon";
    if (obj) {
      this.pb_ids = obj.pb_ids ? obj.pb_ids : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.pb_ids = obj.pb_ids ? obj.pb_ids : [];
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      pb_ids: this.pb_ids
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof PactBoonFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: PactBoonFeature) {
    this.pb_ids = [...copyMe.pb_ids];
  }

  clone(drop_ids = false): PactBoonFeature {
    const new_obj = new PactBoonFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * Changes skills to use a different ability score instead of the default
 */
export class SkillAbilityOverrideFeature extends FloatingFeature {
  skill_ids: string[] = [];
  ability_score: AbilityScoreType | "None" = "None";

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Skill Ability Override";
    if (obj) {
      this.skill_ids = obj.skill_ids ? obj.skill_ids : [];
      this.ability_score = obj.ability_score && obj.ability_score !== "" ? obj.ability_score : "None";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.skill_ids = obj.skill_ids ? obj.skill_ids : [];
  //     this.ability_score = obj.ability_score && obj.ability_score !== "" ? obj.ability_score : "None";
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      skill_ids: this.skill_ids,
      ability_score: this.ability_score,
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SkillAbilityOverrideFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: SkillAbilityOverrideFeature) {
    this.skill_ids = [...copyMe.skill_ids];
    this.ability_score = copyMe.ability_score;
  }

  clone(drop_ids = false): SkillAbilityOverrideFeature {
    const new_obj = new SkillAbilityOverrideFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * This disables actions that fit the limits.  
 * It's meant to go on some negative conditions.
 */
export class DisableActionsFeature extends FloatingFeature {
  apply_to: ("Weapons" | "Abilities" | "Spells")[] = [];
  weapon_limit: WeaponLimitation = new WeaponLimitation();
  ability_limit: AbilityLimitation = new AbilityLimitation();
  spell_limit: SpellLimitation = new SpellLimitation();

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Disable Actions";
    if (obj) {
      // if (obj.name === "Suffocate") {
      //   debugger;
      // }
      this.apply_to = obj.apply_to ? obj.apply_to : ["Weapons"];
      if (obj.weapon_limit) {
        this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
      }
      if (obj.ability_limit) {
        this.ability_limit = new AbilityLimitation(obj.ability_limit);
      }
      if (obj.spell_limit) {
        this.spell_limit = new SpellLimitation(obj.spell_limit);
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     // if (obj.name === "Suffocate") {
  //     //   debugger;
  //     // }
  //     this.apply_to = obj.apply_to ? obj.apply_to : ["Weapons"];
  //     if (obj.weapon_limit) {
  //       this.weapon_limit = new WeaponLimitation(obj.weapon_limit);
  //     }
  //     if (obj.ability_limit) {
  //       this.ability_limit = new AbilityLimitation(obj.ability_limit);
  //     }
  //     if (obj.spell_limit) {
  //       this.spell_limit = new SpellLimitation(obj.spell_limit);
  //     }
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    // debugger;
    return {
      ...db_obj,
      apply_to: this.apply_to,
      weapon_limit: this.weapon_limit.toDBObj(),
      ability_limit: this.ability_limit.toDBObj(),
      spell_limit: this.spell_limit.toDBObj(),
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof DisableActionsFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: DisableActionsFeature) {
    this.apply_to = [...copyMe.apply_to];
    this.weapon_limit = copyMe.weapon_limit.clone();
    this.ability_limit = copyMe.ability_limit.clone();
    this.spell_limit = copyMe.spell_limit.clone();
  }

  clone(drop_ids = false): DisableActionsFeature {
    const new_obj = new DisableActionsFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class IgnoreStealthDisadvantage extends FloatingFeature {
  armor_type_ids: string[] = [];

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Ignore Stealth Disadvantage";
    if (obj) {
      this.armor_type_ids = obj.armor_type_ids ? obj.armor_type_ids : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.armor_type_ids = obj.armor_type_ids ? obj.armor_type_ids : [];
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      armor_type_ids: this.armor_type_ids,
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof IgnoreStealthDisadvantage) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: IgnoreStealthDisadvantage) {
    this.armor_type_ids = copyMe.armor_type_ids;
  }

  clone(drop_ids = false): IgnoreStealthDisadvantage {
    const new_obj = new IgnoreStealthDisadvantage();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * This is used to give a character options for its size.
 */
export class SizeOptions extends FloatingFeature {
  ignore_char_size = false; // If this is true, then the size the character has from its race is ignored, and only the options provided are used.
  size_options: SizeType[] = []; // For if it uses specific options
  default_size: SizeType = 0; // 0 means keep previous. If not an option then go with closest.

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Size Options";
    if (obj) {
      this.ignore_char_size = obj.ignore_char_size ? obj.ignore_char_size : false;
      this.size_options = obj.size_options ? obj.size_options : [];
      this.default_size = obj.default_size ? obj.default_size : 0;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.ignore_char_size = obj.ignore_char_size ? obj.ignore_char_size : false;
  //     this.size_options = obj.size_options ? obj.size_options : [];
  //     this.default_size = obj.default_size ? obj.default_size : 0;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      ignore_char_size: this.ignore_char_size,
      size_options: this.size_options,
      default_size: this.default_size
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SizeOptions) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: SizeOptions) {
    this.ignore_char_size = copyMe.ignore_char_size;
    this.size_options = [...copyMe.size_options];
    this.default_size = copyMe.default_size;
  }

  clone(drop_ids = false): SizeOptions {
    const new_obj = new SizeOptions();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * This is used to bypass CharacterLimitations for various features.
 * For example: Magister Attunement allows them to attune to any item 
 * that normally would be limited by class to a Full Caster.
 */
export class LimitSpoofFeature extends FloatingFeature {
  limits_for: string[] = []; // This is for the type of object that the limit is coming from. Can be Any
  // This limit is used to describe the kind of limits it is allowing the character to pretend that they fit.
  limit_spoof: CharacterLimitation = new CharacterLimitation();

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Limit Spoof";
    if (obj) {
      this.limits_for = obj.limits_for ? obj.limits_for : [];
      this.limit_spoof = obj.limit_spoof ? new CharacterLimitation(obj.limit_spoof) : new CharacterLimitation();
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.limits_for = obj.limits_for ? obj.limits_for : [];
  //     this.limit_spoof = obj.limit_spoof ? new CharacterLimitation(obj.limit_spoof) : new CharacterLimitation();
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      limits_for: this.limits_for,
      limit_spoof: this.limit_spoof.toDBObj()
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof LimitSpoofFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: LimitSpoofFeature) {
    this.limits_for = [...copyMe.limits_for];
    this.limit_spoof = copyMe.limit_spoof.clone();
  }

  clone(drop_ids = false): LimitSpoofFeature {
    const new_obj = new LimitSpoofFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}


/**
 * This is used to keep track of the known (and therefore usable) Magic Item Plans of the new Artificer, 
 * as currently outlined in the new UA.
 * 
 * The full description of the feature is below the class.
 * 
 * It uses limits based on Artificer level, 
 */
export class SpecialLearnable extends FloatingFeature {
  limits_for: string[] = []; // This is for the type of object that the limit is coming from. Can be Any
  // This limit is used to describe the kind of limits it is allowing the character to pretend that they fit.
  limit_spoof: CharacterLimitation = new CharacterLimitation();

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Special Learnable";
    if (obj) {
      this.limits_for = obj.limits_for ? obj.limits_for : [];
      this.limit_spoof = obj.limit_spoof ? new CharacterLimitation(obj.limit_spoof) : new CharacterLimitation();
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.limits_for = obj.limits_for ? obj.limits_for : [];
  //     this.limit_spoof = obj.limit_spoof ? new CharacterLimitation(obj.limit_spoof) : new CharacterLimitation();
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      limits_for: this.limits_for,
      limit_spoof: this.limit_spoof.toDBObj()
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof LimitSpoofFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: LimitSpoofFeature) {
    this.limits_for = [...copyMe.limits_for];
    this.limit_spoof = copyMe.limit_spoof.clone();
  }

  clone(drop_ids = false): LimitSpoofFeature {
    const new_obj = new LimitSpoofFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// You have learned arcane plans that you use to make magic items.

// <b>Plans Known.</b> When you gain this feature, choose four plans to learn from the Magic Item Plans (Artificer Level 2+) table (see the Dungeon Master’s Guide for the items’ descriptions). Bag of Holding, Cap of Water Breathing, Sending Stones, and Wand of the War Mage +1 are recommended. Whenever you gain an Artificer level, you can replace one of the plans you know with a new plan for which you qualify.

// You learn another plan of your choice when you reach certain Artificer levels, as shown in the Plans Known column of the Artificer Features table.

// <b>Creating an Item.</b> When you finish a Long Rest, you can create one or two different magic items if you have Tinker’s Tools in hand. Each item is based on one of the plans you know for this feature.

// If a created item requires Attunement, you can attune yourself to it the instant you create it. If you decide to attune to the item later, you must do so using the normal process for Attunement. 

// When you reach certain Artificer levels specified in the Magic Items column of the Artificer Features table, the number of magic items you can create at the end of a Long Rest increases. Each item you create must be based on a different plan you know.

// If you try to exceed your maximum number of magic items for this feature, the oldest item vanishes, and then the new item appears.

// <b>Duration.</b> A magic item created by this feature functions exactly like the normal magic item, except its magic isn’t permanent. When you die, the magic item vanishes after 1d4 of days. If an item that you created with this feature is a container, such as a Bag of Holding, and it vanishes, its contents harmlessly appear in and around its space.

// <b>MAGIC ITEM PLANS (ARTIFICER LEVEL 2+)</b>
// <table><tr><th>Magic Item Plan</th><th>Attunement</th></tr>
// <tr><td>Alchemy Jug</td><td>No</td></tr>
// <tr><td>Bag of Holding</td><td>No</td></tr>
// <tr><td>Cap of Water Breathing</td><td>No</td></tr>
// <tr><td>Common magic item that isn’t a Potion, a Scroll, or cursed (you can learn this option multiple times and must select a different item each time; each item selected counts as a different plan)</td><td>Varies</td></tr>
// <tr><td>Goggles of Night</td><td>No</td></tr>
// <tr><td>Rope of Climbing</td><td>No</td></tr>
// <tr><td>Sending Stones</td><td>No</td></tr>
// <tr><td>Shield +1</td><td>No</td></tr>
// <tr><td>Wand of Magic Detection</td><td>No</td></tr>
// <tr><td>Wand of Secrets</td><td>No</td></tr>
// <tr><td>Wand of the War Mage +1</td><td>Yes</td></tr>
// <tr><td>Weapon +1</td><td>No</td></tr>
// </table>

// <b>MAGIC ITEM PLANS (ARTIFICER LEVEL 6+)</b>
// <table><tr><th>Magic Item Plan</th><th>Attunement</th></tr>
// <tr><td>Armor +1</td><td>No</td></tr>
// <tr><td>Boots of Elvenkind</td><td>No</td></tr>
// <tr><td>Cloak of Elvenkind</td><td>Yes</td></tr>
// <tr><td>Cloak of the Manta Ray</td><td>No</td></tr>
// <tr><td>Eyes of Charming</td><td>Yes</td></tr>
// <tr><td>Gloves of Thievery</td><td>No</td></tr>
// <tr><td>Lantern of Revealing</td><td>No</td></tr>
// <tr><td>Pipes of Haunting</td><td>No</td></tr>
// <tr><td>Uncommon Armor, Wand, or Weapon that isn’t cursed (you can learn this option multiple times and must select a different item each time; each item selected counts as a different plan)</td><td>Varies</td></tr>
// </table>

// <b>MAGIC ITEM PLANS (ARTIFICER LEVEL 10+)</b>
// <table><tr><th>Magic Item Plan</th><th>Attunement</th></tr>
// <tr><td>Uncommon Ring or Wondrous Item that isn’t cursed (you can learn this option multiple times and must select a different item each time; each item selected counts as a different plan)</td><td>Varies</td></tr>
// </table>

// <b>MAGIC ITEM PLANS (ARTIFICER LEVEL 14+)</b>
// <table><tr><th>Magic Item Plan</th><th>Attunement</th></tr>
// <tr><td>Rare Armor, Ring, Wand, Weapon, or Wondrous Item that isn’t cursed (you can learn this option multiple times and must select a different item each time; each item selected counts as a different plan)</td><td>Varies</td></tr>
// </table>


/**
 * This alters the rules for resting.
 * 
 * It has choices of Short or Long, and lets you apply a multiplier to the healing of Rests.
 * 
 */
export class RestAlterationFeature extends FloatingFeature {
  rest_types: ("Short" | "Long")[] = [];
  healing_multiplier: UpgradableNumber = new UpgradableNumber();

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Rest Alteration";
    if (obj) {
      this.rest_types = obj.rest_types ? obj.rest_types : [];
      this.healing_multiplier = obj.healing_multiplier ? new UpgradableNumber(obj.healing_multiplier) : new UpgradableNumber();
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.rest_types = obj.rest_types ? obj.rest_types : [];
  //     this.healing_multiplier = obj.healing_multiplier ? new UpgradableNumber(obj.healing_multiplier) : new UpgradableNumber();
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      rest_types: this.rest_types,
      healing_multiplier: this.healing_multiplier.toDBObj()
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof RestAlterationFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: RestAlterationFeature) {
    this.rest_types = [...copyMe.rest_types];
    this.healing_multiplier = copyMe.healing_multiplier.clone();
  }

  clone(drop_ids = false): RestAlterationFeature {
    const new_obj = new RestAlterationFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class CreatureTypeFeature extends FloatingFeature {
  creature_type: CreatureType = "None"; // This is your new Creature Type

  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Creature Type";
    if (obj) {
      this.creature_type = obj.creature_type ? obj.creature_type : "None";
    }
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      creature_type: this.creature_type,
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof CreatureTypeFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: CreatureTypeFeature) {
    this.creature_type = copyMe.creature_type;
  }

  clone(drop_ids = false): CreatureTypeFeature {
    const new_obj = new CreatureTypeFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}